import moment from 'moment'
import type {
  SortIdentifierObject,
  SortInstanceObject,
  SortIntIdentifierObject,
  SortNameObject,
  SortObject,
  SortTextObject,
  SortValueObject,
} from '@/helpers/Sorting.interfaces'
import { parseTime } from '@/helpers/Date.helpers'
import { DateFormat } from '@/interfaces/Date.interfaces'

// Number

const _sortNumber = function (a?: unknown, b?: unknown): number {
  if (a && b) {
    return +a - +b
  } else if (a) {
    return 1
  } else if (b) {
    return -1
  }
  return 0
}

export const compareNullableNumber = (a: number | null, b: number | null): number => {
  if (a === null) {
    return b === null ? 0 : 1
  }
  if (b === null) {
    return -1
  }
  return a - b
}

const _sortNumberAttr = function (a: SortObject, b: SortObject, attr: string): number {
  if (a[attr] && b[attr]) {
    return +a[attr] - +b[attr]
  } else if (a[attr]) {
    return 1
  } else if (b[attr]) {
    return -1
  }
  return 0
}

export const sortInitialLevel = (a: SortObject, b: SortObject): number => _sortNumberAttr(b, a, 'initial_level')

export const sortTargetLevel = (a: SortObject, b: SortObject): number => _sortNumberAttr(b, a, 'target_level')

export const sortReductionLevel = (a: SortObject, b: SortObject): number => _sortNumberAttr(b, a, 'reduction_level')

export const sortValue = <T>({ value: a }: SortValueObject<T>, { value: b }: SortValueObject<T>): number =>
  _sortNumber(a, b)

export const sortLowValue = (a: SortObject, b: SortObject): number => _sortNumberAttr(a, b, 'low_value')

export const sortOrder = (a: SortObject, b: SortObject): number => _sortNumberAttr(a, b, 'order')

export const sortControlLevel = (a: SortObject, b: SortObject): number => _sortNumberAttr(a, b, 'control_order')

export const sortDashboardGroup = (a: SortObject, b: SortObject): number => _sortNumberAttr(a, b, 'dashboard_order')

export const sortControl = (a: SortObject, b: SortObject): number => _sortNumberAttr(a, b, 'control_group_order')

export const sortControlGroup = (a: SortObject, b: SortObject): number => _sortNumberAttr(a, b, 'policy_order')

// String

/**
 * Case-sensitive string compare via localeCompare. Falsy values (null, undefined, '') are pushed to the start.
 * For null-aware, case-insensitive ordering with nulls at the end use `compareNullableString` instead.
 */
export const sortString = (a: string | unknown, b: string | unknown): number => {
  if (a && b && typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b)
  } else if (a) {
    return 1
  } else if (b) {
    return -1
  }
  return 0
}

/**
 * Case- and accent-insensitive compare (`sensitivity: 'base'`, so 'foo' === 'FOO' === 'fóo'). Nulls go to the end.
 * For strict case-sensitive ordering use `sortString` instead.
 */
export const compareNullableString = (a: string | null, b: string | null): number => {
  if (a === null) {
    return b === null ? 0 : 1
  }
  if (b === null) {
    return -1
  }
  return a.localeCompare(b, undefined, { sensitivity: 'base' })
}

const _sortStringAttr = function (a: SortObject, b: SortObject, attr: string): number {
  return sortString(a[attr], b[attr])
}

export const sortName = <T>(a: SortNameObject<T>, b: SortNameObject<T>): number => _sortStringAttr(a, b, 'name')

export const sortInstance = <T>(a: SortInstanceObject<T>, b: SortInstanceObject<T>) => _sortStringAttr(a, b, 'instance')

export const sortIdentifier = <T>(a: SortIdentifierObject<T>, b: SortIdentifierObject<T>) =>
  _sortStringAttr(a, b, 'identifier')

export const sortText = <T>(a: SortTextObject<T>, b: SortTextObject<T>) => _sortStringAttr(a, b, 'text')

export const sortPublicIdentifier = (a: SortObject, b: SortObject) => {
  if (a.public && !b.public) {
    return 1
  } else if (b.public && !a.public) {
    return -1
  } else if (a.identifier && b.identifier) {
    return _sortStringAttr(a, b, 'identifier')
  } else {
    return _sortStringAttr(a, b, 'name')
  }
}

export const sortPublic = (a: SortObject, b: SortObject) => {
  if (a.public && !b.public) {
    return 1
  } else if (b.public && !a.public) {
    return -1
  } else {
    return _sortStringAttr(a, b, 'name')
  }
}

// Date

const _sortDate = function (a: SortObject, b: SortObject, attr: string) {
  const date_a = moment(a[attr] as string, 'DD/MM/YYYY')
  const date_b = moment(b[attr] as string, 'DD/MM/YYYY')
  if (date_a && date_b) {
    return date_a.valueOf() - date_b.valueOf()
  }
}

const _sortDateTime = function (a: SortObject, b: SortObject, attr: string) {
  const date_a = moment(a[attr] as string, 'DD/MM/YYYY HH:mm:ss')
  const date_b = moment(b[attr] as string, 'DD/MM/YYYY HH:mm:ss')
  if (date_a && date_b) {
    return date_a.valueOf() - date_b.valueOf()
  }
}

export const sortDate = (a: SortObject, b: SortObject) => _sortDate(a, b, 'date')

export const sortDateCreated = (a: SortObject, b: SortObject) => _sortDate(a, b, 'date_created')

export const sortDateTime = (a: SortObject, b: SortObject, attr = 'timestamp') => _sortDateTime(a, b, attr)

export const compareNullableDate =
  (format: string = DateFormat.FR) =>
  (a: string | null, b: string | null): number =>
    compareNullableNumber(parseTime(a, format), parseTime(b, format))

export const compareNullableDateFr = compareNullableDate()

// Other

export const sortIntIdentifier = <T>(a: SortIntIdentifierObject<T>, b: SortIntIdentifierObject<T>) => {
  const nums1 = a.identifier.split('.')
  const nums2 = b.identifier.split('.')

  for (let i = 0; i < nums1.length; i++) {
    if (nums2[i]) {
      // assuming 5..2 is invalid
      if (nums1[i] !== nums2[i]) {
        return +nums1[i] - +nums2[i]
      } // else continue
    } else {
      return 1 // no second number in b
    }
  }
  return -1 // was missing case b.len > a.len
}
